/**
 * Parses a CLP filter query string into a filters object.
 * Format: "id1=v1,v2&id2=v3"
 *
 * Uses URLSearchParams for robust key/value splitting and automatic decoding.
 * Comma-separated values within each key are split into arrays.
 * Duplicate keys are merged (values are concatenated).
 */
export function parseFilterString(filterStr: string | undefined | null): Record<string, string[]> {
  if (!filterStr) return {}

  const filters: Record<string, string[]> = {}
  const params = new URLSearchParams(filterStr)

  params.forEach((rawValue, key) => {
    if (!key || !rawValue) return

    const values = rawValue
      .split(',')
      .map(v => v.trim())
      .filter(Boolean)

    if (values.length > 0) {
      const existing = filters[key]
      filters[key] = existing ? [...existing, ...values] : values
    }
  })

  return filters
}

/**
 * Converts a filters object to CLP query string format (e.g., "id1=v1,v2&id2=v3").
 * Keys and individual values are URI-encoded to safely handle special characters.
 */
export function serializeFilters(filters: Record<string, string[]>): string | undefined {
  const filterGroups: string[] = []

  for (const [id, values] of Object.entries(filters)) {
    if (values.length > 0) {
      const encodedId = encodeURIComponent(id)
      const encodedValues = values.map(v => encodeURIComponent(v)).join(',')
      filterGroups.push(`${encodedId}=${encodedValues}`)
    }
  }

  return filterGroups.length > 0 ? filterGroups.join('&') : undefined
}
